import React, { useEffect } from 'react'
import {
  AccountDtoPermission,
  AccountDtoRoles,
  AdopterDto,
  AdopterDtoProfileHasBeenAccepted,
  AdopterFollowUpDto,
  AdopterFollowUpDtoType,
  AdopterFollowUpDtoVerdict,
} from '../../../interactors/gen/backendClient'
import { Add } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Fab, Dialog, DialogContent, Box, Typography, TextField, DialogActions, Button } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { fullName } from '../../../domain/Account/AccountDisplay'
import {
  getReadableAdopterFollowUpType,
  getColorForAdopterFollowUpType,
  getColorForAdopterFollowUpVerdict,
  getReadableAdopterFollowUpVerdict,
} from '../../../domain/Adopters/AccountDisplay'
import { useAccountsStore } from '../../../store/AccountsStore'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { PATHS } from '../../PATHS'
import { ColoredChipSelectField } from '../../common/ColoredChipSelectField'
import { ControlledAutoCompleteWithCustomValue } from '../../common/ControlledAutocompleteWithCustomValue'
import { ControlledDateField } from '../../common/ControlledDateField'
import { spacingItem } from '../../theme'
import dayjs from 'dayjs'

type FormParams = {
  type: AdopterFollowUpDtoType
  date: string
  verdict: AdopterFollowUpDtoVerdict
  interlocutorAccountId: string
  description?: string
}

interface Props {
  adopter: AdopterDto
  followUps: Array<AdopterFollowUpDto>
  setFollowUps: (contactFollowUps: Array<AdopterFollowUpDto>) => void
  alreadyAccepted?: boolean
}

export const NewFollowUpModal: React.FC<Props> = ({ adopter, followUps, setFollowUps, alreadyAccepted = false }) => {
  const queryParams = new URLSearchParams(location.search)
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()

  const allMembers = accountsStore.members.filter((account) => !account.roles.includes(AccountDtoRoles.Veterinary))

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormParams>({
    defaultValues: {
      type: undefined,
      date: '',
      description: '',
      verdict: undefined,
      interlocutorAccountId: queryParams.get('memberInCharge') || '',
    },
    mode: 'onChange',
  })

  const onSubmit = (data: FormParams) => {
    const newFollowUp = {
      ...data,
      date: dayjs(data.date).toISOString(),
    }
    setFollowUps([...followUps, newFollowUp as AdopterFollowUpDto])
    closeDialog()
    reset()
  }

  useEffect(() => {
    queryParams.get('card') === 'newFollowUp' && openDialog()
  }, [queryParams.get('card')])

  const interlocutorAccountId = watch('interlocutorAccountId')
  useEffect(() => {
    if (interlocutorAccountId === 'addAccount') {
      reset({ interlocutorAccountId: undefined })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=suivi-adoptant/${adopter.id}&role=${AccountDtoRoles.Member}&card=newFollowUp`
      )
    }
  }, [interlocutorAccountId])

  const isReader = accountsStore.connectedAccount?.permission === AccountDtoPermission.Read

  return (
    <>
      <Fab
        color="primary"
        variant="extended"
        onClick={openDialog}
        disabled={adopter.profileHasBeenAccepted === AdopterDtoProfileHasBeenAccepted.FormRefused || isReader}
      >
        <Add sx={{ mr: 1 }} />
        Ajouter une prise de contact
      </Fab>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box>
              <Typography variant="h4" component="h2" marginBottom={4}>
                Ajouter une prise de contact
              </Typography>
              <ColoredChipSelectField
                sx={{ ...spacingItem, width: '100%' }}
                control={control}
                error={errors.type}
                fieldName="type"
                size="small"
                label="Type de suivi"
                options={Object.values(AdopterFollowUpDtoType).map((status: AdopterFollowUpDtoType) => ({
                  label: getReadableAdopterFollowUpType(status),
                  value: status,
                  color: getColorForAdopterFollowUpType(status),
                }))}
                requiredRule={'Le type de suivi est requis'}
              />

              <ControlledDateField
                control={control}
                validate={(value) => isValidPastDateCoherence(value) || value === null}
                error={errors.date}
                fieldName={'date'}
                label="Date"
                requiredRule={'La date est requise'}
                size="small"
                sx={spacingItem}
              />

              <ControlledAutoCompleteWithCustomValue
                control={control}
                fieldName={'interlocutorAccountId'}
                size="small"
                options={[
                  { value: 'addAccount', label: 'Ajouter un membre' },
                  ...allMembers
                    .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
                    .map((option) => ({ label: fullName(option), value: option.id })),
                ]}
                label="Interlocuteur"
                error={errors.interlocutorAccountId}
                requiredRule={"L'interlocuteur est requis"}
                defaultValue={''}
                sx={spacingItem}
                highlight
              />

              {!alreadyAccepted && (
                <ColoredChipSelectField
                  sx={{ ...spacingItem, width: '100%' }}
                  control={control}
                  error={errors.verdict}
                  fieldName="verdict"
                  size="small"
                  label="Nouvelle décision"
                  options={Object.values(AdopterFollowUpDtoVerdict).map((status: AdopterFollowUpDtoVerdict) => ({
                    label: getReadableAdopterFollowUpVerdict(status),
                    value: status,
                    color: getColorForAdopterFollowUpVerdict(status),
                  }))}
                  requiredRule={'La décision est requise'}
                />
              )}

              <TextField
                id="description-input"
                label="Détails"
                type="text"
                fullWidth
                {...register('description')}
                sx={spacingItem}
                size="small"
                multiline
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-testid="cancel" onClick={closeDialog}>
              Annuler
            </Button>
            <LoadingButton data-testid="apply-changes" variant="contained" type="submit">
              Valider
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
